import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  BookOpen,
  Edit2,
  FileText,
  Calendar,
  Tag,
  Lock,
  Unlock,
  GraduationCap,
  List,
} from 'lucide-react';
import { Card, Button, Badge, StatCard } from '@azyntor-labs/nexus-ui';
import { questionBanksApi } from '@/api/endpoints';
import { format } from 'date-fns';

export const QuestionBankDetails = () => {
  const { bankId } = useParams<{ bankId: string }>();
  const navigate = useNavigate();

  // Fetch question bank details
  const { data: response, isLoading } = useQuery({
    queryKey: ['question-bank', bankId],
    queryFn: () => questionBanksApi.get(bankId!),
    enabled: !!bankId,
  });

  const bank = response?.data?.data;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-white/60">Loading question bank details...</p>
        </div>
      </div>
    );
  }

  if (!bank) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <BookOpen className="w-16 h-16 text-white/20 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Question Bank Not Found</h2>
          <p className="text-white/60 mb-6">The question bank you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/teacher/question-banks')}>
            Back to Question Banks
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<ArrowLeft className="w-4 h-4" />}
            onClick={() => navigate('/teacher/question-banks')}
          >
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-white">{bank.name}</h1>
            <p className="text-white/50 mt-1">{bank.description || 'No description provided'}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Link to={`/teacher/question-banks/${bank.id}/questions`}>
            <Button variant="secondary" leftIcon={<List className="w-4 h-4" />}>
              View Questions
            </Button>
          </Link>
          <Button
            leftIcon={<Edit2 className="w-4 h-4" />}
            onClick={() => navigate('/teacher/question-banks')}
          >
            Edit Bank
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Questions"
          value={bank.question_count || 0}
          icon={<FileText />}
          color="teal"
        />
        <StatCard
          title="Subject"
          value={bank.subject || 'N/A'}
          icon={<BookOpen />}
          color="green"
        />
        <StatCard
          title="Grade Level"
          value={bank.grade_level || 'N/A'}
          icon={<GraduationCap />}
          color="teal"
        />
        <StatCard
          title="Visibility"
          value={bank.is_public ? 'Public' : 'Private'}
          icon={bank.is_public ? <Unlock /> : <Lock />}
          color={bank.is_public ? 'green' : 'orange'}
        />
      </div>

      {/* Details Card */}
      <Card>
        <div className="p-6 space-y-6">
          <div>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center">
              <BookOpen className="w-5 h-5 mr-2" />
              Question Bank Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name */}
              <div>
                <label className="text-sm font-medium text-white/50 block mb-2">
                  Bank Name
                </label>
                <p className="text-white font-medium">{bank.name}</p>
              </div>

              {/* Subject */}
              <div>
                <label className="text-sm font-medium text-white/50 block mb-2">
                  Subject
                </label>
                <p className="text-white font-medium">{bank.subject || 'Not specified'}</p>
              </div>

              {/* Grade Level */}
              <div>
                <label className="text-sm font-medium text-white/50 block mb-2">
                  Grade Level
                </label>
                <p className="text-white font-medium">{bank.grade_level || 'Not specified'}</p>
              </div>

              {/* Question Count */}
              <div>
                <label className="text-sm font-medium text-white/50 block mb-2">
                  Total Questions
                </label>
                <p className="text-white font-medium">{bank.question_count || 0}</p>
              </div>

              {/* Visibility */}
              <div>
                <label className="text-sm font-medium text-white/50 block mb-2">
                  Visibility
                </label>
                <Badge variant={bank.is_public ? 'success' : 'secondary'}>
                  {bank.is_public ? (
                    <>
                      <Unlock className="w-3 h-3 mr-1" />
                      Public
                    </>
                  ) : (
                    <>
                      <Lock className="w-3 h-3 mr-1" />
                      Private
                    </>
                  )}
                </Badge>
              </div>

              {/* Created Date */}
              <div>
                <label className="text-sm font-medium text-white/50 block mb-2 flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  Created Date
                </label>
                <p className="text-white font-medium">
                  {format(new Date(bank.created_at), 'MMMM dd, yyyy')}
                </p>
              </div>

              {/* Updated Date */}
              {bank.updated_at && (
                <div>
                  <label className="text-sm font-medium text-white/50 block mb-2 flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    Last Updated
                  </label>
                  <p className="text-white font-medium">
                    {format(new Date(bank.updated_at), 'MMMM dd, yyyy')}
                  </p>
                </div>
              )}

              {/* Created By */}
              {bank.created_by && (
                <div>
                  <label className="text-sm font-medium text-white/50 block mb-2">
                    Created By
                  </label>
                  <p className="text-white font-medium">{bank.created_by}</p>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          {bank.description && (
            <div className="pt-6 border-t border-white/[0.06]">
              <label className="text-sm font-medium text-white/50 block mb-2">
                Description
              </label>
              <p className="text-white/80 leading-relaxed">{bank.description}</p>
            </div>
          )}

          {/* Tags */}
          {bank.tags && bank.tags.length > 0 && (
            <div className="pt-6 border-t border-white/[0.06]">
              <label className="text-sm font-medium text-white/50 block mb-3 flex items-center">
                <Tag className="w-4 h-4 mr-1" />
                Tags
              </label>
              <div className="flex flex-wrap gap-2">
                {bank.tags.map((tag: string, index: number) => (
                  <Badge key={index} variant="info" size="sm">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}

        </div>
      </Card>

      {/* Actions Card */}
      <Card>
        <div className="p-6">
          <h2 className="text-xl font-bold text-white mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to={`/teacher/question-banks/${bank.id}/questions`} className="block">
              <button className="w-full p-4 bg-gradient-to-br from-teal-500/10 to-teal-600/10 hover:from-teal-500/20 hover:to-teal-600/20 border border-teal-500/20 rounded-lg transition-all duration-200 group">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-teal-500/20 rounded-lg group-hover:bg-teal-500/30 transition-colors">
                    <List className="w-5 h-5 text-teal-400" />
                  </div>
                  <div className="text-left">
                    <div className="font-semibold text-white">View All Questions</div>
                    <div className="text-sm text-white/50">
                      Browse {bank.question_count || 0} questions
                    </div>
                  </div>
                </div>
              </button>
            </Link>

            <button
              onClick={() => navigate('/teacher/question-banks')}
              className="w-full p-4 bg-gradient-to-br from-blue-500/10 to-blue-600/10 hover:from-blue-500/20 hover:to-blue-600/20 border border-blue-500/20 rounded-lg transition-all duration-200 group"
            >
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-500/20 rounded-lg group-hover:bg-blue-500/30 transition-colors">
                  <Edit2 className="w-5 h-5 text-blue-400" />
                </div>
                <div className="text-left">
                  <div className="font-semibold text-white">Edit Bank</div>
                  <div className="text-sm text-white/50">Modify bank details</div>
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate('/teacher/questions/create')}
              className="w-full p-4 bg-gradient-to-br from-emerald-500/10 to-emerald-600/10 hover:from-emerald-500/20 hover:to-emerald-600/20 border border-emerald-500/20 rounded-lg transition-all duration-200 group"
            >
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-emerald-500/20 rounded-lg group-hover:bg-emerald-500/30 transition-colors">
                  <FileText className="w-5 h-5 text-emerald-400" />
                </div>
                <div className="text-left">
                  <div className="font-semibold text-white">Add Question</div>
                  <div className="text-sm text-white/50">Create new question</div>
                </div>
              </div>
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
};
